import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCustomerReview,
  createCustomerTicket,
  fetchCustomer,
  fetchCustomerReviews,
  fetchCustomerTickets,
  updateCustomer,
} from '../../api/customersModule';
import { deleteReview, updateReview } from '../../api/reviewsModule';
import { deleteTicket, updateTicket } from '../../api/ticketsModule';
import RoleGuard from '../../components/RoleGuard';
import { useToast } from '../../components/ToastContext';
import useModuleAuth from '../../hooks/useModuleAuth';

const sources = ['WEB', 'BOOKING', 'AIRBNB', 'OTHER'];
const ticketStatuses = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
const ticketPriorities = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const Tabs = ({ active, onChange }) => (
  <div className="flex gap-2 border-b border-slate-200">
    {['reviews', 'tickets'].map((tab) => (
      <button
        key={tab}
        type="button"
        onClick={() => onChange(tab)}
        className={`px-4 py-2 text-sm font-semibold ${
          active === tab ? 'border-b-2 border-emerald-500 text-emerald-700' : 'text-slate-600'
        }`}
      >
        {tab === 'reviews' ? 'Reviews' : 'Support tickets'}
      </button>
    ))}
  </div>
);

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
    <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-lg">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600"
        >
          Close
        </button>
      </div>
      <div className="pt-4">{children}</div>
    </div>
  </div>
);

const Stars = ({ value }) => (
  <div className="flex items-center gap-1 text-amber-500">
    {Array.from({ length: 5 }).map((_, idx) => (
      <span key={idx}>{idx < value ? '*' : '-'}</span>
    ))}
  </div>
);

const CustomerDetail = () => {
  const { id } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isAdmin } = useModuleAuth();
  const [activeTab, setActiveTab] = useState('reviews');
  const [editOpen, setEditOpen] = useState(false);
  const [reviewModal, setReviewModal] = useState(null);
  const [ticketModal, setTicketModal] = useState(false);

  const { data: customerData, isLoading, error: customerError } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => fetchCustomer(id),
    enabled: !!id,
  });

  const { data: reviewsData, error: reviewsError } = useQuery({
    queryKey: ['customerReviews', id],
    queryFn: () => fetchCustomerReviews(id),
    enabled: !!id,
  });

  const { data: ticketsData, error: ticketsError } = useQuery({
    queryKey: ['customerTickets', id],
    queryFn: () => fetchCustomerTickets(id),
    enabled: !!id,
  });

  const customer = customerData?.data;
  const reviews = reviewsData?.data || [];
  const tickets = ticketsData?.data || [];

  const updateCustomerMutation = useMutation({
    mutationFn: (payload) => updateCustomer(id, payload),
    onSuccess: () => {
      toast.success('Customer updated');
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      setEditOpen(false);
    },
    onError: (err) => toast.error(err?.message || 'Update failed'),
  });

  const reviewMutation = useMutation({
    mutationFn: ({ reviewId, payload }) =>
      reviewId ? updateReview(reviewId, payload) : createCustomerReview(id, payload),
    onSuccess: () => {
      toast.success('Review saved');
      queryClient.invalidateQueries({ queryKey: ['customerReviews', id] });
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      setReviewModal(null);
    },
    onError: (err) => toast.error(err?.message || 'Review update failed'),
  });

  const deleteReviewMutation = useMutation({
    mutationFn: deleteReview,
    onSuccess: () => {
      toast.success('Review deleted');
      queryClient.invalidateQueries({ queryKey: ['customerReviews', id] });
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    },
    onError: (err) => toast.error(err?.message || 'Delete failed'),
  });

  const ticketMutation = useMutation({
    mutationFn: (payload) => createCustomerTicket(id, payload),
    onSuccess: () => {
      toast.success('Ticket created');
      queryClient.invalidateQueries({ queryKey: ['customerTickets', id] });
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      setTicketModal(false);
    },
    onError: (err) => toast.error(err?.message || 'Ticket create failed'),
  });

  const updateTicketMutation = useMutation({
    mutationFn: ({ ticketId, payload }) => updateTicket(ticketId, payload),
    onSuccess: () => {
      toast.success('Ticket updated');
      queryClient.invalidateQueries({ queryKey: ['customerTickets', id] });
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    },
    onError: (err) => toast.error(err?.message || 'Ticket update failed'),
  });

  const deleteTicketMutation = useMutation({
    mutationFn: deleteTicket,
    onSuccess: () => {
      toast.success('Ticket removed');
      queryClient.invalidateQueries({ queryKey: ['customerTickets', id] });
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
    },
    onError: (err) => toast.error(err?.message || 'Ticket delete failed'),
  });

  const stats = customer?.stats || {};
  const fullName = useMemo(() => `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim(), [customer]);

  if (isLoading) {
    return <div className="text-sm text-slate-500">Loading customer...</div>;
  }

  if (customerError) {
    return (
      <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        {customerError.message || 'Failed to load customer'}
      </div>
    );
  }

  if (!customer) {
    return <div className="text-sm text-slate-500">Customer not found.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-700">Customer</p>
            <h1 className="text-2xl font-bold text-slate-900">{fullName}</h1>
            <p className="text-sm text-slate-600">{customer.email}</p>
          </div>
          <RoleGuard>
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Edit customer
            </button>
          </RoleGuard>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-4">
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Total reviews</p>
            <p className="text-lg font-semibold text-slate-900">{stats.reviewsCount || 0}</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Average rating</p>
            <p className="text-lg font-semibold text-slate-900">{(stats.avgRating || 0).toFixed(1)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Open tickets</p>
            <p className="text-lg font-semibold text-slate-900">{stats.openTickets || 0}</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Status</p>
            <p className="text-lg font-semibold text-slate-900">{customer.status}</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <Tabs active={activeTab} onChange={setActiveTab} />

        {activeTab === 'reviews' && (
          <div className="pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">{reviews.length} reviews</p>
              <RoleGuard>
                <button
                  type="button"
                  onClick={() => setReviewModal({})}
                  className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Add review
                </button>
              </RoleGuard>
            </div>
            {reviewsError && (
              <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {reviewsError.message || 'Failed to load reviews'}
              </div>
            )}
            {!reviewsError && reviews.length === 0 && (
              <div className="text-sm text-slate-500">No reviews yet.</div>
            )}
            <div className="space-y-3">
              {reviews.map((review) => (
                <div key={review.id} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{review.title}</p>
                      <p className="text-xs text-slate-500">{review.source}</p>
                    </div>
                    <Stars value={review.rating} />
                  </div>
                  <p className="mt-2 text-sm text-slate-700">{review.comment}</p>
                  {isAdmin && (
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setReviewModal(review)}
                        className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Delete this review?')) {
                            deleteReviewMutation.mutate(review.id);
                          }
                        }}
                        className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">{tickets.length} tickets</p>
              <RoleGuard allow={['ADMIN', 'STAFF']}>
                <button
                  type="button"
                  onClick={() => setTicketModal(true)}
                  className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  New ticket
                </button>
              </RoleGuard>
            </div>
            {ticketsError && (
              <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {ticketsError.message || 'Failed to load tickets'}
              </div>
            )}
            {!ticketsError && tickets.length === 0 && (
              <div className="text-sm text-slate-500">No tickets yet.</div>
            )}
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div key={ticket.id} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{ticket.subject}</p>
                      <p className="text-xs text-slate-500">{ticket.message}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {isAdmin ? (
                        <>
                          <select
                            value={ticket.status}
                            onChange={(event) =>
                              updateTicketMutation.mutate({
                                ticketId: ticket.id,
                                payload: { status: event.target.value, priority: ticket.priority },
                              })
                            }
                            className="rounded-lg border border-slate-200 px-2 py-1"
                          >
                            {ticketStatuses.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                          <select
                            value={ticket.priority}
                            onChange={(event) =>
                              updateTicketMutation.mutate({
                                ticketId: ticket.id,
                                payload: { status: ticket.status, priority: event.target.value },
                              })
                            }
                            className="rounded-lg border border-slate-200 px-2 py-1"
                          >
                            {ticketPriorities.map((priority) => (
                              <option key={priority} value={priority}>
                                {priority}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <>
                          <span className="rounded-full border border-slate-200 px-2 py-1 font-semibold text-slate-600">
                            {ticket.status}
                          </span>
                          <span className="rounded-full border border-slate-200 px-2 py-1 font-semibold text-slate-600">
                            {ticket.priority}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Delete this ticket?')) {
                            deleteTicketMutation.mutate(ticket.id);
                          }
                        }}
                        className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editOpen && (
        <Modal title="Edit customer" onClose={() => setEditOpen(false)}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              updateCustomerMutation.mutate({
                firstName: formData.get('firstName'),
                lastName: formData.get('lastName'),
                email: formData.get('email'),
                phone: formData.get('phone'),
                status: formData.get('status'),
              });
            }}
            className="space-y-3"
          >
            <div className="grid gap-3 md:grid-cols-2">
              <input
                name="firstName"
                defaultValue={customer.firstName}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
              <input
                name="lastName"
                defaultValue={customer.lastName}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <input
              name="email"
              type="email"
              defaultValue={customer.email}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <input
              name="phone"
              defaultValue={customer.phone || ''}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <select
              name="status"
              defaultValue={customer.status}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {['ACTIVE', 'INACTIVE'].map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={updateCustomerMutation.isPending}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {updateCustomerMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      )}

      {reviewModal !== null && (
        <Modal title={reviewModal.id ? 'Edit review' : 'Add review'} onClose={() => setReviewModal(null)}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              const payload = {
                rating: Number(formData.get('rating')),
                title: formData.get('title'),
                comment: formData.get('comment'),
                source: formData.get('source'),
              };
              reviewMutation.mutate({ reviewId: reviewModal.id, payload });
            }}
            className="space-y-3"
          >
            <input
              name="title"
              defaultValue={reviewModal.title || ''}
              placeholder="Review title"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <textarea
              name="comment"
              defaultValue={reviewModal.comment || ''}
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              placeholder="Comment"
            />
            <div className="grid gap-3 md:grid-cols-2">
              <select
                name="rating"
                defaultValue={reviewModal.rating || 5}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              >
                {[5, 4, 3, 2, 1].map((rating) => (
                  <option key={rating} value={rating}>
                    {rating} stars
                  </option>
                ))}
              </select>
              <select
                name="source"
                defaultValue={reviewModal.source || 'WEB'}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              >
                {sources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={reviewMutation.isPending}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {reviewMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      )}

      {ticketModal && (
        <Modal title="New ticket" onClose={() => setTicketModal(false)}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              ticketMutation.mutate({
                subject: formData.get('subject'),
                message: formData.get('message'),
                priority: formData.get('priority'),
              });
            }}
            className="space-y-3"
          >
            <input
              name="subject"
              placeholder="Ticket subject"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <textarea
              name="message"
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              placeholder="Describe the issue"
            />
            <select
              name="priority"
              defaultValue="MEDIUM"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {ticketPriorities.map((priority) => (
                <option key={priority} value={priority}>
                  {priority}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={ticketMutation.isPending}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {ticketMutation.isPending ? 'Saving...' : 'Create ticket'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default CustomerDetail;
